import { BodyText, Col } from '@policygenius/mortar';
import { useMemo } from 'react';
import { Form } from 'react-final-form';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';

import GlobalLoader from 'components/GlobalLoader';
import NavigatorContent from 'components/NavigatorContent';
import { InitialValues } from 'forms/types';
import useNavigatorTracking from 'hooks/useNavigatorTracking';
import Steps from 'hooks/useNavigatorTracking/steps';
import { BENEFIT_PERIOD_PATH } from 'routes/paths';

import {
  SUB_HEADING,
  AFTER_IMAGE_TEXT,
  HEADING,
  LIST_TEXT_ITEMS,
  WAITING_PERIOD_DIAGRAM_ALT,
  WAITING_PERIOD_DIAGRAM_URL,
} from './constants';
import WaitingPeriodForm from './form';
import { WaitingPeriodFormValues } from './form/types';
import { useGetSurveyWaitingPeriodQuery } from './graphql/GetWaitingPeriod';
import { useUpdateWaitingPeriodMutation } from './graphql/UpdateWaitingPeriod';
import styles from './styles';
import buildInitialValues from './utils/buildInitialValues';
import { validate } from './utils/validate';

const useStyles = createUseStyles(styles);

const WaitingPeriod = () => {
  const trackStepCompleted = useNavigatorTracking(Steps.WaitingPeriod);
  const classes = useStyles();
  const { data, loading } = useGetSurveyWaitingPeriodQuery();
  const initialValues = useMemo(() => buildInitialValues(data), [data]);
  const history = useHistory();
  const [updateWaitingPeriod] = useUpdateWaitingPeriodMutation({
    onCompleted: () => {
      trackStepCompleted();
      history.push(BENEFIT_PERIOD_PATH);
    },
  });

  const onSubmit = async (values: WaitingPeriodFormValues) => {
    await updateWaitingPeriod({
      variables: {
        input: values,
      },
    });
  };

  if (loading) {
    return <GlobalLoader />;
  }

  return (
    <NavigatorContent heading={HEADING} subHeading={SUB_HEADING} title={Steps.WaitingPeriod}>
      <Col push={{ small: 0, medium: 2, large: 3 }} span={{ small: 4, medium: 8, large: 6 }}>
        <img
          className={classes.image}
          alt={WAITING_PERIOD_DIAGRAM_ALT}
          src={WAITING_PERIOD_DIAGRAM_URL}
        />
        <BodyText size="large" className={classes.bodyText}>
          {AFTER_IMAGE_TEXT}
        </BodyText>
        <ul className={classes.list}>
          {LIST_TEXT_ITEMS.map((listItem) => (
            <BodyText as="li" size="medium" key={listItem} className={classes.listItem}>
              {listItem}
            </BodyText>
          ))}
        </ul>
      </Col>
      <Form<WaitingPeriodFormValues, InitialValues<WaitingPeriodFormValues>>
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
      >
        {(formProps) => <WaitingPeriodForm {...formProps} />}
      </Form>
    </NavigatorContent>
  );
};

export default WaitingPeriod;
